import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX = 5 * 1024 * 1024;

// Center-crop to a 512px square JPEG so every photo looks the same everywhere
const cropSquare = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const side = Math.min(img.width, img.height);
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 512;
    canvas.getContext("2d").drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, 512, 512);
    URL.revokeObjectURL(img.src);
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("crop failed"))), "image/jpeg", 0.9);
  };
  img.onerror = () => reject(new Error("That file doesn't look like a valid image"));
  img.src = URL.createObjectURL(file);
});

export const ProfilePhotoCard = () => {
  const { user, refreshUser } = useAuth();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Please choose a JPG, PNG or WebP image");
    if (file.size > MAX) return toast.error("Photo must be 5MB or smaller");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", await cropSquare(file), "photo.jpg");
      await api.post("/profile/photo", form);
      await refreshUser();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(errMsg(err, err.message || "Could not upload your photo"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/profile/photo");
      await refreshUser();
      toast.success("Photo removed");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mm-card p-4 mb-6 flex items-center gap-4" data-testid="photo-card">
      <div className="relative">
        <Avatar url={user.photo_url} name={user.name} size={72} testId="photo-avatar" />
        {busy && <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center"><Loader2 className="animate-spin text-white" size={22} /></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={input} type="file" accept={TYPES.join(",")} className="hidden" onChange={upload} data-testid="photo-file-input" />
        <button className="mm-btn !py-2 !px-4" disabled={busy} onClick={() => input.current?.click()} data-testid="photo-upload"><Camera size={16} /> Upload photo</button>
        {user.photo_url && (
          <button className="mm-btn-ghost !py-2 !px-4" disabled={busy} onClick={remove} data-testid="photo-remove"><Trash2 size={16} /> Remove</button>
        )}
      </div>
    </div>
  );
};
